import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { api } from '@/api/client'
import type { FeedbackCategory } from '@/api/types'
import type { Article } from '@/help'
import { Markdown } from '@/components/Markdown'

/**
 * The "?" panel: the article for the screen you are on, a way into the Help
 * Center, and Send feedback. Slides in from the right; on a phone it covers
 * the screen. Esc, the close button or a tap on the backdrop closes it, and
 * following a link inside it closes it too.
 */
export function HelpPanel({ article, onClose }: { article: Article; onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    closeRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="help-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside className="help-panel" role="dialog" aria-modal="true" aria-labelledby="help-panel-title">
        <div className="help-panel-head">
          <div>
            <div className="help-kicker">Help</div>
            <h2 id="help-panel-title">{article.title}</h2>
            <p className="muted">{article.summary}</p>
          </div>
          <button ref={closeRef} type="button" className="btn btn-secondary btn-sm" onClick={onClose} aria-label="Close help">
            Close
          </button>
        </div>
        <div className="help-panel-body">
          <Markdown source={article.body} onNavigate={onClose} />
          <div className="help-panel-links">
            <Link to={`/help/${article.slug}`} onClick={onClose}>
              Open in Help
            </Link>
            <Link to="/help" onClick={onClose}>
              All help topics
            </Link>
            <Link to="/help/faq" onClick={onClose}>
              FAQ
            </Link>
          </div>
          <div className="help-feedback-box">
            <FeedbackForm compact />
          </div>
        </div>
      </aside>
    </div>
  )
}

const PLACEHOLDER: Record<FeedbackCategory, string> = {
  Bug: 'What did you expect, and what happened? e.g. "Per diem total for Juan looks wrong"',
  Question: 'What would you like to know?',
  Idea: 'What would make this better?',
}

/** A bug, question or idea, sent with the page you are on. */
export function FeedbackForm({ compact }: { compact?: boolean }) {
  const { pathname, search } = useLocation()
  const [category, setCategory] = useState<FeedbackCategory>('Bug')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [sent, setSent] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setErr(null)
    try {
      // From the Help Center the page is the help article itself, which is
      // still the most useful thing to record.
      await api.sendFeedback({ category, message: message.trim(), page: `${pathname}${search}` })
      setSent(true)
      setMessage('')
    } catch (e2) {
      setErr(
        navigator.onLine === false
          ? 'No signal right now. Your note is still here — try Send again when you have a connection.'
          : (e2 as Error).message || 'Could not send. Try again in a moment.',
      )
    } finally {
      setBusy(false)
    }
  }

  if (sent) {
    return (
      <div className="help-feedback-sent">
        <span>Thanks — sent, with the page you were on. Replies show under “Your feedback” in Help.</span>
        <button type="button" className="btn-plain" onClick={() => setSent(false)}>
          Send another
        </button>
      </div>
    )
  }

  return (
    <form onSubmit={submit} className="help-feedback">
      <div className="help-feedback-row">
        <strong>Send feedback</strong>
        {!compact && <span className="muted"> — a problem, a question or an idea. We see the page you are on.</span>}
        <select
          aria-label="Kind of feedback"
          value={category}
          onChange={(e) => setCategory(e.target.value as FeedbackCategory)}
        >
          <option value="Bug">Something’s wrong</option>
          <option value="Question">Question</option>
          <option value="Idea">Idea</option>
        </select>
      </div>
      <textarea
        rows={compact ? 3 : 4}
        required
        minLength={3}
        maxLength={4000}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder={PLACEHOLDER[category]}
      />
      {err && <div className="alert alert-error">{err}</div>}
      <div className="help-feedback-actions">
        <button type="submit" className="btn btn-sm" disabled={busy || message.trim().length < 3}>
          {busy ? 'Sending…' : 'Send'}
        </button>
      </div>
    </form>
  )
}
